import { Link } from "react-router-dom";
import { ChevronRight, Check, ArrowRight, Phone } from "lucide-react";
import { trackPhoneClick } from "@/lib/analytics";
import { Rich } from "@/components/Rich";

/** Hero + breadcrumb bar shared by the event landing pages. */
export const EventPageHero = ({ overline, h1, intro, ctaDevis, ctaCall, image, alt, crumbs, phoneSource }) => (
  <>
    <section className="relative min-h-[62vh] flex items-end overflow-hidden -mt-[148px] pt-[148px]">
      {image && <img src={image} alt={alt} className="absolute inset-0 w-full h-full object-cover" />}
      <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/30 to-black/65" />
      <div className="relative z-10 max-w-[1500px] mx-auto w-full px-6 md:px-12 lg:px-16 py-16 md:py-24">
        <p className="overline text-[#E6CFA1]">{overline}</p>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-6 leading-[1.05] text-white font-light max-w-4xl">
          {h1}
        </h1>
        <Rich as="p" value={intro} className="mt-8 text-white/85 text-base md:text-lg max-w-3xl leading-relaxed" />
        <div className="mt-10 flex flex-wrap gap-4">
          <a href="#devis" data-testid="hero-cta-devis" className="btn-gold">
            {ctaDevis} <ArrowRight size={14} />
          </a>
          <a
            href="tel:+33182001867"
            data-testid="hero-cta-phone"
            onClick={() => trackPhoneClick(phoneSource)}
            className="btn-ghost-light"
          >
            <Phone size={14} /> {ctaCall}
          </a>
        </div>
      </div>
    </section>
    <div className="bg-[#F6F0E7] border-b border-[#D9D3C5]">
      <div className="max-w-[1500px] mx-auto px-6 md:px-12 lg:px-16 py-4">
        <Breadcrumbs items={crumbs} testid="page-breadcrumbs" />
      </div>
    </div>
  </>
);

/** Section heading, shared by the event landing pages. */
export const SectionTitle = ({ children, id }) => (
  <h2 id={id} className="font-display text-3xl md:text-4xl lg:text-5xl text-[#203240] font-light leading-tight">
    {children}
  </h2>
);

/** Real HTML table: `rows` is an array of [label, value] pairs. */
export const DataTable = ({ caption, head, rows, testid }) => (
  <div className="overflow-x-auto">
    <table data-testid={testid} className="w-full border-collapse text-left">
      <caption className="sr-only">{caption}</caption>
      {head && (
        <thead>
          <tr>
            {head.map((h) => (
              <th
                key={h}
                scope="col"
                className="border-b border-[#B8956A] py-3 pr-6 text-xs uppercase tracking-[0.18em] text-[#B8956A] font-normal"
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
      )}
      <tbody>
        {rows.map(([a, b]) => (
          <tr key={a} className="align-top border-b border-[#D9D3C5]">
            <th
              scope="row"
              className="py-4 pr-6 w-[38%] md:w-[28%] text-xs uppercase tracking-[0.18em] text-[#697585] font-normal"
            >
              {a}
            </th>
            <td className="py-4 text-[#203240] text-sm md:text-base leading-relaxed"><Rich value={b} /></td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

/** Real HTML table with N columns: `rows` = arrays of cells, first cell is the row header. */
export const MatrixTable = ({ caption, head, rows, testid }) => (
  <div className="overflow-x-auto">
    <table data-testid={testid} className="w-full border-collapse text-left min-w-[560px]">
      <caption className="sr-only">{caption}</caption>
      <thead>
        <tr>
          {head.map((h, i) => (
            <th
              key={i}
              scope="col"
              className="border-b border-[#B8956A] py-3 pr-6 text-xs uppercase tracking-[0.18em] text-[#B8956A] font-normal"
            >
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((cells) => (
          <tr key={cells[0]} className="align-top border-b border-[#D9D3C5]">
            <th
              scope="row"
              className="py-4 pr-6 w-[26%] text-xs uppercase tracking-[0.18em] text-[#697585] font-normal"
            >
              {cells[0]}
            </th>
            {cells.slice(1).map((cell, i) => (
              <td key={i} className="py-4 pr-6 text-[#203240] text-sm md:text-base leading-relaxed">
                <Rich value={cell} />
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

/** Bulleted list where each item has a bold label and a body. */
export const LabelledList = ({ items, testid }) => (
  <ul className="space-y-5" data-testid={testid}>
    {items.map((it) => (
      <li key={it.label} className="flex items-start gap-3 text-[#3D4A5A] leading-relaxed">
        <Check size={18} className="text-[#B8956A] mt-1 shrink-0" />
        <span>
          <Rich value={it.label} className="text-[#203240]" />
          <Rich value={it.body} />
        </span>
      </li>
    ))}
  </ul>
);

/** Visible breadcrumb trail. `items` = [{ label, to }] — last item has no `to`. */
export const Breadcrumbs = ({ items, testid = "breadcrumbs" }) => (
  <nav aria-label="Fil d'Ariane" data-testid={testid} className="text-xs uppercase tracking-[0.18em]">
    <ol className="flex flex-wrap items-center gap-2 text-[#697585]">
      {items.map((it, i) => (
        <li key={it.label} className="flex items-center gap-2">
          {it.to ? (
            <Link to={it.to} className="hover:text-[#B8956A] transition-colors">{it.label}</Link>
          ) : (
            <span aria-current="page" className="text-[#B8956A]">{it.label}</span>
          )}
          {i < items.length - 1 && <ChevronRight size={12} className="text-[#D9D3C5]" />}
        </li>
      ))}
    </ol>
  </nav>
);
